import { useRef } from 'react';
import { Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload, faSave } from '@fortawesome/free-solid-svg-icons';

export default function SettingsView({ settings, onChange }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const handleChange = (key, value) => {
    // Permette di svuotare il campo durante la digitazione senza forzarlo a 0
    if (value === '') {
      onChange({ ...settings, [key]: '' });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({ ...settings, [key]: numValue });
    }
  };

  const handleExport = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(settings, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute('download', 'repaircalc_settings.json');
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        const parsed = JSON.parse(content);
        if (typeof parsed === 'object' && parsed !== null) {
          onChange({ ...settings, ...parsed });
          alert(t('repaircalc.settings.importSuccess'));
        }
      } catch (err) {
        alert(t('repaircalc.settings.importError'));
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const marginTypeOptions = [
    { key: 'fixed', label: t('repaircalc.settings.fixed') },
    { key: 'percentage', label: t('repaircalc.settings.percentage') },
    { key: 'auto', label: t('repaircalc.settings.auto') }
  ];

  return (
    <div>
      <div className="repaircalc-section mb-3">
        <h5 className="mb-1">{t('repaircalc.settings.marginsTitle')}</h5>
        <p className="text-muted">{t('repaircalc.settings.marginsDesc')}</p>

        <div className="margin-type-selector mb-3">
          {marginTypeOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`margin-type-btn ${settings.marginType === opt.key ? 'active' : ''}`}
              onClick={() => onChange({ ...settings, marginType: opt.key })}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {settings.marginType === 'auto' && (
          <div className="alert alert-info mb-3">
            <Form.Label className="fw-medium">{t('repaircalc.settings.autoThreshold')}</Form.Label>
            <p className="small mb-2">{t('repaircalc.settings.autoThresholdDesc')}</p>
            <InputGroup style={{ maxWidth: '200px' }}>
              <InputGroup.Text>€</InputGroup.Text>
              <Form.Control
                type="number"
                value={settings.autoMarginThreshold ?? ''}
                onChange={(e) => handleChange('autoMarginThreshold', e.target.value)}
                placeholder="30"
              />
            </InputGroup>
          </div>
        )}

        {(settings.marginType === 'fixed' || settings.marginType === 'auto') && (
          <div className="mb-3">
            {settings.marginType === 'auto' && <h6 className="text-muted text-uppercase">{t('repaircalc.settings.fixedMargins')}</h6>}
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginEasy')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginEasy ?? ''}
                onChange={(e) => handleChange('marginEasy', e.target.value)}
                placeholder="20"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginMedium')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginMedium ?? ''}
                onChange={(e) => handleChange('marginMedium', e.target.value)}
                placeholder="40"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginHard')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginHard ?? ''}
                onChange={(e) => handleChange('marginHard', e.target.value)}
                placeholder="80"
              />
            </Form.Group>
          </div>
        )}

        {(settings.marginType === 'percentage' || settings.marginType === 'auto') && (
          <div>
            {settings.marginType === 'auto' && <h6 className="text-muted text-uppercase">{t('repaircalc.settings.percentageMargins')}</h6>}
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginEasyPct')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginEasyPct ?? ''}
                onChange={(e) => handleChange('marginEasyPct', e.target.value)}
                placeholder="20"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginMediumPct')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginMediumPct ?? ''}
                onChange={(e) => handleChange('marginMediumPct', e.target.value)}
                placeholder="35"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t('repaircalc.settings.marginHardPct')}</Form.Label>
              <Form.Control
                type="number"
                value={settings.marginHardPct ?? ''}
                onChange={(e) => handleChange('marginHardPct', e.target.value)}
                placeholder="50"
              />
            </Form.Group>
          </div>
        )}
      </div>

      <div className="repaircalc-section mb-3">
        <h5 className="mb-3">{t('repaircalc.settings.fiscalTitle')}</h5>
        <Form.Group className="mb-3">
          <Form.Label>{t('repaircalc.settings.ivaPercentage')}</Form.Label>
          <Form.Control
            type="number"
            value={settings.ivaPercentage ?? ''}
            onChange={(e) => handleChange('ivaPercentage', e.target.value)}
            placeholder="22"
          />
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>{t('repaircalc.settings.cardFeePercentage')}</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                value={settings.cardFeePercentage ?? ''}
                onChange={(e) => handleChange('cardFeePercentage', e.target.value)}
                placeholder="1.5"
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>{t('repaircalc.settings.cardFixedFee')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={settings.cardFixedFee ?? ''}
                onChange={(e) => handleChange('cardFixedFee', e.target.value)}
                placeholder="0.20"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-medium">{t('repaircalc.settings.autoCard')}</div>
            <div className="text-muted small">{t('repaircalc.settings.autoCardDesc')}</div>
          </div>
          <Form.Check
            type="switch"
            id="default-card-payment-switch"
            checked={!!settings.defaultCardPayment}
            onChange={() => onChange({ ...settings, defaultCardPayment: !settings.defaultCardPayment })}
            className="custom-switch"
          />
        </div>
      </div>

      <div className="repaircalc-section mb-3">
        <h5 className="mb-1">{t('repaircalc.settings.saveRestore')}</h5>
        <p className="text-muted">{t('repaircalc.settings.saveRestoreDesc')}</p>
        <div className="d-flex">
          <Button variant="outline-secondary" className="me-2" onClick={handleExport}>
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            {t('repaircalc.settings.export')}
          </Button>
          <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()}>
            <FontAwesomeIcon icon={faUpload} className="me-1" />
            {t('repaircalc.settings.import')}
          </Button>
          <input
            type="file"
            accept=".json"
            onChange={handleImport}
            ref={fileInputRef}
            hidden
          />
        </div>
      </div>

      <div className="text-center text-muted small">
        <FontAwesomeIcon icon={faSave} className="me-1" />
        {t('repaircalc.settings.autoSaved')}
      </div>
    </div>
  );
}
